import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import LaneNav from "../components/LaneNav";
import { toast } from "sonner";
import { Camera, Upload, ArrowLeft, FilePdf, ImageSquare, CircleNotch, CaretDown, EnvelopeSimple, DeviceMobile, Desktop } from "@phosphor-icons/react";
import api from "../lib/api";

// Accept everything the OCR pipeline (GPT-5.2 vision + pdf2image) can handle.
// image/* covers every mobile camera roll format incl. heic/heif on iOS, webp on Android, bmp/tiff edge cases.
const ACCEPT = "image/*,application/pdf";
const MAX_BYTES = 10 * 1024 * 1024;

const HOW_TO_EMAIL = [
  {
    id: "gmail-mobile",
    label: "Gmail on iPhone or Android",
    icon: DeviceMobile,
    steps: [
      "Open the receipt email in the Gmail app.",
      "Tap the three-dot menu (top right of the email).",
      "Tap 'Print' - Gmail builds a PDF preview.",
      "Tap the share icon, then 'Save to Files' (iPhone) or 'Save as PDF' (Android).",
      "Come back here, tap 'Upload a file', and pick the PDF you just saved.",
    ],
  },
  {
    id: "apple-mail-iphone",
    label: "Apple Mail on iPhone or iPad",
    icon: DeviceMobile,
    steps: [
      "Open the receipt email in the Mail app.",
      "Tap the reply arrow (bottom right), then 'Print'.",
      "Pinch out on the preview thumbnail - it expands into a full-page PDF.",
      "Tap the share icon, then 'Save to Files'.",
      "Come back here, tap 'Upload a file', and pick the PDF.",
    ],
  },
  {
    id: "outlook-mobile",
    label: "Outlook on iPhone or Android",
    icon: DeviceMobile,
    steps: [
      "Open the receipt email in the Outlook app.",
      "Tap the three-dot menu, then 'Print conversation' (iPhone) or 'Print' (Android).",
      "From the print preview, choose 'Save as PDF'.",
      "Come back here and upload the PDF.",
    ],
  },
  {
    id: "gmail-desktop",
    label: "Gmail in a browser",
    icon: Desktop,
    steps: [
      "Open the receipt email.",
      "Click the printer icon in the top-right corner of the email.",
      "In the print dialog, change the destination to 'Save as PDF'.",
      "Save the file, then drag it into the upload area below.",
    ],
  },
  {
    id: "screenshot",
    label: "Quick alternative - screenshot",
    icon: ImageSquare,
    steps: [
      "Open the receipt email so the whole receipt is visible.",
      "iPhone: side button + volume up. Android: power + volume down. Desktop: print-screen or Cmd+Shift+4.",
      "Come back here and upload the screenshot - works just as well for the AI reader.",
      "If the receipt is long, two or three screenshots is fine - upload them one at a time.",
    ],
  },
];

export default function AddVaultItem() {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [openHowTo, setOpenHowTo] = useState(null);
  const fileInput = useRef(null);
  const cameraInput = useRef(null);
  const navigate = useNavigate();

  const submit = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) return toast.error("File too large. Max 10 MB.");
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/vault/upload", form);
      toast.success(`Saved - ${data.retailer || "receipt"} added to your vault`);
      navigate(`/dashboard/vault/${data.vault_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f7f8]">
      <Header />
      <main className="max-w-[760px] mx-auto px-6 py-12" data-testid="vault-add-page">
        <LaneNav active="vault" />
        <Link to="/dashboard/vault" className="pg-overline inline-flex items-center gap-2 text-[#52525b] hover:text-black mb-6" data-testid="vault-add-back">
          <ArrowLeft size={12} /> Back to vault
        </Link>
        <div className="pg-overline">New receipt</div>
        <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">Add a receipt to your vault.</h1>
        <p className="text-[#52525b] mt-3 max-w-md text-sm leading-relaxed">
          Snap a photo of a paper receipt, or drop in an email PDF. We'll auto-extract the retailer, items, total, and warranty length - and you can edit anything we get wrong.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mt-10">
          <button
            onClick={() => cameraInput.current?.click()}
            disabled={busy}
            className="bg-white p-8 text-left hover:bg-[#f7f7f8] transition-colors disabled:opacity-60 flex flex-col gap-3"
            data-testid="vault-add-camera-btn"
          >
            <Camera size={32} weight="duotone" color="#002FA7" />
            <div>
              <h3 className="font-heading font-bold tracking-tight">Take a photo</h3>
              <p className="text-xs text-[#52525b] mt-1">Phone camera · paper receipts</p>
            </div>
          </button>
          <button
            onClick={() => fileInput.current?.click()}
            disabled={busy}
            className="bg-white p-8 text-left hover:bg-[#f7f7f8] transition-colors disabled:opacity-60 flex flex-col gap-3"
            data-testid="vault-add-upload-btn"
          >
            <Upload size={32} weight="duotone" color="#002FA7" />
            <div>
              <h3 className="font-heading font-bold tracking-tight">Upload a file</h3>
              <p className="text-xs text-[#52525b] mt-1">PDF, JPG, PNG, HEIC, WebP, screenshot · max 10 MB</p>
            </div>
          </button>
        </div>

        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) submit(f); }}
          className={`mt-6 border-2 border-dashed ${dragOver ? "border-[#002FA7] bg-[#EFF1FB]" : "border-[#e4e4e7] bg-white"} p-10 text-center transition-colors block cursor-pointer`}
          data-testid="vault-add-dropzone"
        >
          <FilePdf size={28} weight="duotone" className="mx-auto text-[#52525b]" />
          <p className="font-mono-pg text-xs uppercase tracking-widest text-[#52525b] mt-3">or drag &amp; drop a file here</p>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => submit(e.target.files?.[0])}
            data-testid="vault-add-file-input"
          />
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => submit(e.target.files?.[0])}
            data-testid="vault-add-camera-input"
          />
        </label>

        {busy && (
          <div className="mt-8 bg-white border border-[#e4e4e7] p-6 flex items-center gap-4" data-testid="vault-add-busy">
            <CircleNotch size={24} weight="bold" className="animate-spin text-[#002FA7]" />
            <div>
              <p className="font-bold">Reading your receipt with AI…</p>
              <p className="text-xs text-[#52525b] mt-1">This usually takes 5-10 seconds.</p>
            </div>
          </div>
        )}

        {/* Email-receipt how-to explainer */}
        <div className="mt-10 bg-white border border-[#e4e4e7]" data-testid="vault-add-howto">
          <div className="p-5 border-b border-[#e4e4e7] flex items-start gap-3">
            <EnvelopeSimple size={22} weight="duotone" color="#002FA7" className="flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-heading font-bold tracking-tight">Got the receipt in an email?</h3>
              <p className="text-[#52525b] text-sm mt-1 leading-relaxed">
                Many NZ retailers email the receipt as plain text with no attachment. Save it as a PDF or screenshot, then upload it above. Pick your email app below for step-by-step instructions.
              </p>
            </div>
          </div>
          <div className="divide-y divide-[#e4e4e7]">
            {HOW_TO_EMAIL.map((h) => {
              const open = openHowTo === h.id;
              const Icon = h.icon;
              return (
                <div key={h.id} data-testid={`howto-${h.id}`}>
                  <button
                    type="button"
                    onClick={() => setOpenHowTo(open ? null : h.id)}
                    className="w-full flex items-center justify-between gap-3 text-left px-5 py-4 hover:bg-[#f7f7f8] transition-colors"
                    data-testid={`howto-toggle-${h.id}`}
                    aria-expanded={open}
                  >
                    <span className="flex items-center gap-3">
                      <Icon size={18} weight="duotone" color="#52525b" />
                      <span className="font-bold text-sm">{h.label}</span>
                    </span>
                    <CaretDown size={14} weight="bold" className={`text-[#52525b] transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 pt-1 bg-[#f7f7f8]" data-testid={`howto-body-${h.id}`}>
                      <ol className="text-sm text-[#0a0a0a] space-y-2 list-decimal pl-5 leading-relaxed">
                        {h.steps.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="p-5 border-t border-[#e4e4e7] bg-[#f7f7f8] flex items-start gap-3">
            <div className="text-[10px] font-bold tracking-widest bg-black text-white px-1.5 py-0.5 uppercase mt-0.5">Soon</div>
            <p className="text-xs text-[#52525b] leading-relaxed">
              Direct email forwarding is coming - you'll be able to forward the receipt straight to your vault from any of your inboxes without saving a PDF first.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
